import React, { useEffect, useRef, useState } from 'react';
import { CalendarDays, Loader2, Phone } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useRcStatus } from './RcStatusChip';
import { useCallCenter } from './callCenter/CallCenterProvider';
import { CalendlyLinkForm } from './MyCalendlyControl';
import { formatPhoneToken } from './boardUtils';
import useAnchoredPopover from './useAnchoredPopover';

// Connections (Nic 2026-10-20): the setup a rep needs green before they can
// work, RingCentral (calls and texts) and their Calendly (bookings), in one
// button. The button always shows both, a phone and a calendar each with its
// own dot, then a label: quiet "Connected" when both are green, otherwise
// the one thing to do (RingCentral first). Clicking
// it always opens a panel with a row for each, RingCentral's red Sign in
// first, so neither one is ever out of sight.
// withCalendly: Calendly is for people who work leads (canWork).

const DOT = {
  good: 'bg-emerald-500',
  bad: 'bg-red-500',
  warn: 'bg-amber-500',
  wait: 'bg-slate-300 dark:bg-slate-600',
};

const Dot = ({ tone, pulse = false }) => (
  <span className="relative flex h-2 w-2 shrink-0">
    {pulse ? (
      <span
        className={cn('absolute inline-flex h-full w-full animate-ping rounded-full opacity-60', DOT[tone])}
      />
    ) : null}
    <span className={cn('relative inline-flex h-2 w-2 rounded-full', DOT[tone])} />
  </span>
);

// One connection on the button: its icon with a status dot on the corner
// (Nic 2026-10-20: "where's the Calendly?", so both always show).
const Mark = ({ icon: Icon, tone, pulse = false, label }) => (
  <span className="relative inline-flex" aria-label={label} title={label}>
    {/* The icon stays neutral; the dot says the state and the label is in
        its color (Nic 2026-10-20). */}
    <Icon size={13} className="text-slate-500 dark:text-slate-400" />
    <span className="absolute -bottom-0.5 -right-1 flex h-[7px] w-[7px]">
      {pulse ? (
        <span className={cn('absolute inline-flex h-full w-full animate-ping rounded-full opacity-60', DOT[tone])} />
      ) : null}
      <span
        className={cn(
          'relative inline-flex h-[7px] w-[7px] rounded-full ring-[1.5px] ring-white dark:ring-surface-card',
          DOT[tone],
        )}
      />
    </span>
  </span>
);

const Row = ({ icon: Icon, title, tone, detail, action }) => (
  <div className="flex items-center gap-3 rounded-xl px-2 py-2.5">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:!bg-white/10 dark:text-slate-300">
      <Icon size={15} />
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 dark:text-slate-100">
        {title}
        <Dot tone={tone} />
      </span>
      <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{detail}</span>
    </span>
    {action}
  </div>
);

const ConnectionsControl = ({ myCalendlyUrl = '', withCalendly = true, apiBase = '/onboarding-board' }) => {
  const rc = useRcStatus(apiBase);
  // Sign out drops the browser phone: never over a live call (review
  // 2026-11-02).
  const { busy: lineBusy } = useCallCenter();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const popStyle = useAnchoredPopover(open, btnRef, 340);

  useEffect(() => {
    if (!open) {
      setEditing(false);
      return undefined;
    }
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    const onDown = (e) => {
      // The panel is position: fixed inside the root, so contains() works.
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const rcOut = rc.state === 'out';
  const rcNoNumber = rc.state === 'no_number';
  const rcChecking = rc.state === 'checking';
  const calOk = !withCalendly || !!myCalendlyUrl;

  // What the button says: the one thing that needs doing, RingCentral first
  // (calls and texts stop without it).
  let tone = 'good';
  let label = 'Connected';
  if (rcOut) {
    tone = 'bad';
    label = rc.signingIn ? 'Signing in' : 'Sign into RingCentral';
  } else if (rcChecking) {
    tone = 'wait';
    label = 'Connections';
  } else if (rcNoNumber) {
    tone = 'warn';
    label = 'No texting number';
  } else if (!calOk) {
    tone = 'warn';
    label = 'Add your Calendly';
  }

  // Always the panel (Nic 2026-10-20: with RingCentral signed out the button
  // used to sign in straight away, so the Calendly row was never seen).
  const onButton = () => setOpen((v) => !v);

  const rcTone = rcOut ? 'bad' : rcChecking ? 'wait' : rcNoNumber ? 'warn' : 'good';
  const rcDetail = rcOut
    ? 'Signed out'
    : rcChecking
      ? 'Checking...'
      : rcNoNumber
        ? 'No texting number'
        : `Signed in · ${formatPhoneToken(rc.fromNumber) || 'your line'}`;

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={onButton}
        title={
          rcOut
            ? 'Not signed into RingCentral in this browser, and calls and texts need it.'
            : 'RingCentral and Calendly: what you need connected to work'
        }
        className={cn(
          'flex items-center gap-1.5 whitespace-nowrap rounded-full bg-white px-3.5 py-1.5 text-[11px] font-medium shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] outline-none transition-[color] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:!bg-surface-card dark:ring-1 dark:ring-white/10',
          rcOut
            ? 'font-semibold text-red-600 dark:text-red-400'
            : tone === 'warn'
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
        )}
      >
        {/* Both connections, each with its own dot, then the one thing to do. */}
        <span className="flex items-center gap-2.5 pr-0.5">
          {rcOut && rc.signingIn ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Mark icon={Phone} tone={rcTone} pulse={rcOut} label={`RingCentral: ${rcDetail}`} />
          )}
          {withCalendly ? (
            <Mark
              icon={CalendarDays}
              tone={myCalendlyUrl ? 'good' : 'warn'}
              label={myCalendlyUrl ? 'Calendly: set' : 'Calendly: not set'}
            />
          ) : null}
        </span>
        <span className="os-act-label hidden sm:inline">{label}</span>
      </button>
      {open && popStyle ? (
        <div
          style={popStyle}
          className="z-[1040] rounded-2xl bg-white p-2 shadow-[0_8px_30px_rgba(15,23,42,0.18)] ring-1 ring-slate-200/70 dark:!bg-surface-elevated dark:ring-white/10"
        >
          <div className="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            Connections
          </div>
          <Row
            icon={Phone}
            title="RingCentral"
            tone={rcTone}
            detail={rcDetail}
            action={
              rcOut ? (
                <button
                  type="button"
                  onClick={rc.signIn}
                  disabled={rc.signingIn}
                  className="shrink-0 whitespace-nowrap rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white outline-none hover:bg-red-700 focus:outline-none disabled:opacity-60"
                >
                  {rc.signingIn ? 'Signing in...' : 'Sign in'}
                </button>
              ) : rcChecking || !rc.signOut ? null : (
                // Sign out (Nic 2026-11-02): a fresh login is how the token
                // picks up a scope the app gained, such as Read Presence.
                <button
                  type="button"
                  onClick={rc.signOut}
                  disabled={!!lineBusy}
                  title={
                    lineBusy
                      ? 'Finish your call first. Signing out drops the phone.'
                      : 'Sign out of RingCentral here. Sign back in to pick up any new permission.'
                  }
                  className="shrink-0 whitespace-nowrap rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 outline-none hover:bg-slate-200 focus:outline-none disabled:opacity-50 dark:!bg-white/10 dark:text-slate-200 dark:hover:!bg-white/15"
                >
                  Sign out
                </button>
              )
            }
          />
          {withCalendly ? (
            <>
              <div className="mx-2 h-px bg-slate-100 dark:!bg-white/10" />
              <Row
                icon={CalendarDays}
                title="My Calendly"
                tone={myCalendlyUrl ? 'good' : 'warn'}
                detail={
                  myCalendlyUrl
                    ? myCalendlyUrl.replace(/^https?:\/\//, '')
                    : 'Not set'
                }
                action={
                  editing ? null : (
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      // Not set: amber like its dot, the way RingCentral's
                      // Sign in matches its red (Nic 2026-10-20).
                      className={cn(
                        'shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold outline-none focus:outline-none',
                        myCalendlyUrl
                          ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:!bg-white/10 dark:text-slate-200 dark:hover:!bg-white/15'
                          : 'bg-amber-500 text-white hover:bg-amber-600',
                      )}
                    >
                      {myCalendlyUrl ? 'Change' : 'Add link'}
                    </button>
                  )
                }
              />
              {editing ? (
                <div className="px-2 pb-2">
                  <CalendlyLinkForm
                    myCalendlyUrl={myCalendlyUrl}
                    apiBase={apiBase}
                    onDone={() => setEditing(false)}
                  />
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ConnectionsControl;
